import { useCurrentUserWorkflowTrackersContext } from '../contexts/useCurrentUserWorkflowTrackersContext';
import {
  useOnTrackerComplete,
  type UseOnTrackerCompleteOptions,
} from './useOnTrackerComplete';

export type UseOnWorkflowTrackerCompleteOptions = Omit<
  UseOnTrackerCompleteOptions,
  'loading' | 'sourceLabel' | 'trackers'
>;

/**
 * Hook that allows components to register a callback that will be called when
 * a workflow tracker of a specific kind completes. Completion is detected when
 * a tracker becomes completed, first arrives as completed, or leaves the active
 * tracker list after it starts.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { refetch } = useQuery(...);
 *
 *   useOnWorkflowTrackerComplete({
 *     kind: WorkflowTrackerKind.DocumentProcessing,
 *     onCompleted: () => {
 *       refetch();
 *     }
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useOnWorkflowTrackerComplete({
  kind,
  onCompleted,
  enabled = true,
  filter,
}: UseOnWorkflowTrackerCompleteOptions) {
  const { loading, trackers } = useCurrentUserWorkflowTrackersContext();

  useOnTrackerComplete({
    kind,
    onCompleted,
    enabled,
    filter,
    loading,
    trackers,
    sourceLabel: 'currentUser',
  });
}
